import { Container, Graphics, Sprite, Text, Texture } from 'pixi.js';
import gsap from 'gsap';
import { Scene } from '../engine/scene';
import { getContext } from '../engine/context';
import { onKeyDown, onKeyUp, isKeyDown } from '../engine/input';
import { WalletStore } from '../stores/WalletStore';
import { between, betweenInt } from '../engine/math';
import { GAME_HEIGHT, GAME_WIDTH, TUNING, type Side } from '../config/tuning';
import { DIFFICULTIES, type Difficulty } from '../config/difficulty';
import { JointController } from '../systems/JointController';
import { LungController } from '../systems/LungController';
import { ChokeController } from '../systems/ChokeController';
import { ScoreController } from '../systems/ScoreController';
import { AIController, type IAIController } from '../systems/AIController';
import { NarcoAIController } from '../systems/NarcoAIController';
import { TurnController, type InhaleEndDetails } from '../systems/TurnController';
import {
  QteController,
  type QteArmedDetails,
  type QteStartDetails,
  type QteWinDetails,
  type QteMissDetails,
  type QteEndDetails,
} from '../systems/QteController';
import { Character } from '../entities/Character';
import { Joint } from '../entities/Joint';
import { Hud } from '../entities/Hud';
import { SmokeEmitter } from '../entities/SmokeEmitter';
import { TouchController } from '../systems/TouchController';
import { isMobile } from '../config/mobile';

interface GameSceneData {
  difficulty?: Difficulty;
}

/**
 * GameScene — the core gameplay. Composes background, characters, joint, HUD,
 * particles, audio, and the TurnController state machine. Keyboard SPACE drives
 * inhale; on mobile a TouchController overlay mirrors that.
 */
export class GameScene extends Scene {
  private difficulty: Difficulty = 'medium';

  private joint!: Joint;
  private playerChar!: Character;
  private aiChar!: Character;
  private hud!: Hud;
  private smoke!: SmokeEmitter;

  private jointCtrl!: JointController;
  private lungCtrl!: LungController;
  private chokeCtrl!: ChokeController;
  private scoreCtrl!: ScoreController;
  private aiCtrl!: IAIController;
  private turnCtrl!: TurnController;
  private qteCtrl!: QteController;

  private hazeOverlay!: Graphics;
  private muteButton!: Sprite;
  private touchCtrl: TouchController | null = null;
  private coinFlipTimeoutHandles: Set<number> = new Set();
  /** Auto-reset the choke face to idle after 3s, per-side. */
  private chokeResetTimers: Map<Side, number> = new Map();

  async onEnter(data?: unknown): Promise<void> {
    this.difficulty = (data as GameSceneData)?.difficulty ?? 'medium';

    this.buildBackground();
    this.buildCharacters();
    this.buildJoint();
    this.smoke = new SmokeEmitter(this.app);
    this.smoke.init();
    this.smoke.container.zIndex = 50;
    this.addChild(this.smoke.container);

    this.hud = new Hud();
    this.hud.container.zIndex = 85;
    this.addChild(this.hud.container);

    this.hazeOverlay = new Graphics();
    this.hazeOverlay.rect(0, 0, GAME_WIDTH, GAME_HEIGHT).fill({ color: 0x000000, alpha: 0.8 });
    this.hazeOverlay.alpha = 0;
    this.hazeOverlay.zIndex = 70;
    this.addChild(this.hazeOverlay);

    this.buildHudControls();
    this.buildSystems();
    this.wireSystems();
    this.bindKeyboard();
    this.bindTouch();

    const ctx = getContext();
    ctx.audio.playMusic('music_chill');
    this.runCoinFlip();
  }

  override onExit(): void {
    super.onExit();
    // Cancel any pending coin-flip timers so a scene re-entry doesn't replay
    // them against destroyed Pixi objects.
    this.coinFlipTimeoutHandles.forEach((h) => window.clearTimeout(h));
    this.coinFlipTimeoutHandles.clear();
    this.chokeResetTimers.forEach((h) => window.clearTimeout(h));
    this.chokeResetTimers.clear();
    this.touchCtrl?.destroy();
    this.touchCtrl = null;
    this.smoke?.destroy();
    this.hud?.destroy();
    const ctx = getContext();
    ctx.audio.stopAllLoops();
    ctx.audio.stopMusic();
  }

  override update(deltaMs: number): void {
    if (!this.turnCtrl) return;
    this.turnCtrl.update(deltaMs);
    this.qteCtrl.update(deltaMs);
    const active = this.turnCtrl.getActiveSide();
    if (active) {
      this.hud.setAtRisk(active, this.turnCtrl.getTurnAtRiskSecs());
      if (this.qteCtrl.getState(active) === 'active') {
        this.hud.updateQteLine(this.qteCtrl.getLinePct(active));
      }
    }
    const state = this.turnCtrl.getState();
    const inhaling = state === 'inhaling-player' || state === 'inhaling-ai';
    if (active && inhaling) {
      this.hud.setCurrentDrag(active, this.turnCtrl.getCurrentDragSecs());
      this.hud.setMultiplier(active, this.turnCtrl.getTurnDragCount());
      const tipPos = this.jointTipWorldPosition();
      this.smoke.trickle(tipPos.x, tipPos.y);
    } else if (active && state.startsWith('combo-window-')) {
      this.hud.setMultiplier(active, this.turnCtrl.getTurnDragCount());
    } else if (!active) {
      this.hud.setMultiplier('player', 0);
      this.hud.setMultiplier('ai', 0);
    }
  }

  // ── Build ────────────────────────────────────────────────────────────────

  private buildBackground(): void {
    // Room wall is the #bg-extended CSS layer (fills the entire viewport).
    // Scene-level sprites below sit on top of it at design-space positions.

    const window = new Sprite(Texture.from('bg_window_light'));
    window.anchor.set(0.5);
    window.position.set(340, 290);
    this.addChild(window);
    gsap.to(window, { alpha: 0.85, duration: 3, yoyo: true, repeat: -1, ease: 'sine.inOut' });

    const poster = new Sprite(Texture.from('bg_poster'));
    poster.anchor.set(0.5);
    poster.position.set(1380, 320);
    this.addChild(poster);

    this.buildLavaLamp();

    const couch = new Sprite(Texture.from('bg_couch'));
    couch.anchor.set(0.5);
    couch.position.set(GAME_WIDTH / 2, GAME_HEIGHT - 280);
    this.addChild(couch);

    const carpet = new Sprite(Texture.from('bg_carpet'));
    carpet.anchor.set(0.5);
    carpet.position.set(GAME_WIDTH / 2, GAME_HEIGHT - 60);
    this.addChild(carpet);

    for (let i = 0; i < 3; i++) {
      const cloud = new Sprite(Texture.from(i % 2 === 0 ? 'smoke_cloud_drift_1' : 'smoke_cloud_drift_2'));
      cloud.anchor.set(0.5);
      cloud.position.set(betweenInt(0, GAME_WIDTH), betweenInt(80, 400));
      cloud.alpha = 0.25;
      cloud.zIndex = 60;
      this.addChild(cloud);
      gsap.to(cloud, {
        x: cloud.x + between(-300, 300),
        duration: between(12, 18),
        yoyo: true,
        repeat: -1,
        ease: 'sine.inOut',
      });
    }
  }

  private buildLavaLamp(): void {
    const lampX = 150;
    const baseY = 800;
    const glassY = 600;

    const glow = new Sprite(Texture.from('bg_lava_lamp_glow'));
    glow.anchor.set(0.5);
    glow.position.set(lampX, glassY + 40);
    glow.alpha = 0.65;
    glow.zIndex = 1;
    this.addChild(glow);
    gsap.to(glow, { alpha: 0.85, duration: 3.5, yoyo: true, repeat: -1, ease: 'sine.inOut' });
    gsap.to(glow.scale, { x: 1.06, y: 1.06, duration: 3.5, yoyo: true, repeat: -1, ease: 'sine.inOut' });

    const base = new Sprite(Texture.from('bg_lava_lamp_base'));
    base.anchor.set(0.5);
    base.position.set(lampX, baseY);
    base.zIndex = 2;
    this.addChild(base);

    const liquid = new Sprite(Texture.from('bg_lava_lamp_liquid'));
    liquid.anchor.set(0.5);
    liquid.position.set(lampX, glassY);
    liquid.zIndex = 3;
    this.addChild(liquid);

    const blobMoves = [
      { startX: lampX - 8, startY: glassY + 80, endX: lampX - 2, endY: glassY - 40, d: 6.2, delay: 0, key: 'bg_lava_lamp_blob_1' },
      { startX: lampX + 12, startY: glassY - 10, endX: lampX + 4, endY: glassY + 90, d: 7.8, delay: 1.2, key: 'bg_lava_lamp_blob_2' },
      { startX: lampX - 4, startY: glassY - 80, endX: lampX + 6, endY: glassY + 20, d: 9.4, delay: 2.4, key: 'bg_lava_lamp_blob_3' },
    ];
    blobMoves.forEach((m) => {
      const b = new Sprite(Texture.from(m.key));
      b.anchor.set(0.5);
      b.position.set(m.startX, m.startY);
      b.zIndex = 4;
      this.addChild(b);
      gsap.to(b, {
        x: m.endX,
        y: m.endY,
        duration: m.d,
        delay: m.delay,
        yoyo: true,
        repeat: -1,
        ease: 'sine.inOut',
      });
      gsap.to(b.scale, {
        x: 1.18,
        y: 0.86,
        duration: m.d,
        delay: m.delay,
        yoyo: true,
        repeat: -1,
        ease: 'sine.inOut',
      });
    });

    const glass = new Sprite(Texture.from('bg_lava_lamp_glass'));
    glass.anchor.set(0.5);
    glass.position.set(lampX, glassY);
    glass.zIndex = 5;
    this.addChild(glass);
  }

  private buildCharacters(): void {
    // Characters sit in the lower third of the canvas, symmetric around centre.
    // xOffset from each edge is ~28% of the width; yBase puts feet ~30% from bottom.
    const xOffset = GAME_WIDTH * 0.28;
    const yBase = GAME_HEIGHT - 340;
    this.playerChar = new Character('player', xOffset, yBase);
    this.playerChar.zIndex = 20;
    // Opponent skin comes from the wallet (default 'ai' = bucket-hat girl).
    const aiSkin = WalletStore.getSelectedSkin();
    this.aiChar = new Character('ai', GAME_WIDTH - xOffset, yBase, aiSkin);
    this.aiChar.zIndex = 20;
    this.addChild(this.playerChar, this.aiChar);
  }

  private buildJoint(): void {
    this.joint = new Joint(0, 0);
    this.joint.zIndex = 40;
    this.addChild(this.joint);
    this.joint.setHolder('player');
    this.placeJointInHand('player');
  }

  private buildHudControls(): void {
    const ctx = getContext();

    // Mute button in bottom-right corner.
    this.muteButton = new Sprite(Texture.from(ctx.audio.isMuted() ? 'hud_mute_off' : 'hud_mute_on'));
    this.muteButton.anchor.set(0.5);
    this.muteButton.position.set(GAME_WIDTH - 60, GAME_HEIGHT - 60);
    this.muteButton.zIndex = 110;
    this.muteButton.eventMode = 'static';
    this.muteButton.cursor = 'pointer';
    this.muteButton.on('pointerdown', () => {
      const muted = ctx.audio.toggleMute();
      this.muteButton.texture = Texture.from(muted ? 'hud_mute_off' : 'hud_mute_on');
    });
    this.addChild(this.muteButton);

    // Menu link in bottom-left corner.
    const menuButton = new Text({
      text: '‹ Menu',
      style: { fontFamily: 'Impact, sans-serif', fontSize: 24, fill: 0x5fdbcf },
    });
    menuButton.anchor.set(0, 0.5);
    menuButton.position.set(60, GAME_HEIGHT - 60);
    menuButton.zIndex = 110;
    menuButton.eventMode = 'static';
    menuButton.cursor = 'pointer';
    menuButton.on('pointerover', () => { menuButton.style.fill = 0xffd23f; });
    menuButton.on('pointerout', () => { menuButton.style.fill = 0x5fdbcf; });
    menuButton.on('pointerdown', () => {
      ctx.audio.play('sfx_click', { volume: 0.55 });
      ctx.sceneManager.start('MenuScene');
    });
    this.addChild(menuButton);
  }

  private buildSystems(): void {
    this.jointCtrl = new JointController();
    this.lungCtrl = new LungController();
    this.chokeCtrl = new ChokeController();
    this.scoreCtrl = new ScoreController();
    const profile = DIFFICULTIES[this.difficulty];
    this.aiCtrl =
      this.difficulty === 'narco'
        ? new NarcoAIController(profile)
        : new AIController(profile);
    this.turnCtrl = new TurnController({
      joint: this.jointCtrl,
      lung: this.lungCtrl,
      choke: this.chokeCtrl,
      score: this.scoreCtrl,
      ai: this.aiCtrl,
    });
    this.qteCtrl = new QteController(
      this.lungCtrl,
      this.chokeCtrl,
      this.jointCtrl,
      this.aiCtrl,
      this.turnCtrl,
    );
  }

  // ── Wire events ──────────────────────────────────────────────────────────

  private wireSystems(): void {
    const ctx = getContext();

    this.jointCtrl.on(JointController.EVT_LENGTH_CHANGED, (pct: number) => {
      this.joint.setLengthPct(pct);
      this.hud.drawJointBar(pct);
    });

    this.lungCtrl.on(LungController.EVT_CHANGED, (side: Side, pct: number) => {
      this.hud.setLung(side, pct);
    });

    this.chokeCtrl.on(ChokeController.EVT_CHANGED, (side: Side, pct: number) => {
      this.hud.setChoke(side, pct);
      const active = this.turnCtrl.getActiveSide();
      if (active === side) {
        if (pct >= TUNING.choke.threshold.heavyWarning) {
          if (this.hazeOverlay.alpha < 0.6) gsap.to(this.hazeOverlay, { alpha: 0.6, duration: 0.2 });
          this.joint.setEmber('warning');
        } else if (pct >= TUNING.choke.threshold.mildWarning) {
          if (Math.abs(this.hazeOverlay.alpha - 0.3) > 0.1) gsap.to(this.hazeOverlay, { alpha: 0.3, duration: 0.2 });
          this.joint.setEmber('warning');
        } else {
          if (this.hazeOverlay.alpha > 0) gsap.to(this.hazeOverlay, { alpha: 0, duration: 0.2 });
          this.joint.setEmber(this.turnCtrl.getState().startsWith('inhaling-') ? 'inhale' : 'idle');
        }
      }
    });

    this.chokeCtrl.on(ChokeController.EVT_WARNING_LEVEL, (side: Side, level: 'safe' | 'mild' | 'heavy') => {
      const char = side === 'player' ? this.playerChar : this.aiChar;
      if (level === 'heavy' || level === 'mild') char.setHead('warning');
    });

    this.scoreCtrl.on(ScoreController.EVT_TOTAL_CHANGED, (side: Side, total: number) => {
      this.hud.setTotal(side, total);
    });

    this.turnCtrl.on(TurnController.EVT_INHALE_START, (side: Side) => {
      const char = side === 'player' ? this.playerChar : this.aiChar;
      char.setHead('inhale');
      char.setArm('holding');
      this.placeJointInHand(side);
      this.joint.setEmber('inhale');
      this.joint.pulseEmber();
      ctx.audio.play('sfx_inhale');
      ctx.audio.playLoop('sfx_ember', 0.35);
      const tip = this.jointTipWorldPosition();
      this.smoke.burst(tip.x, tip.y, 4);
    });

    this.turnCtrl.on(TurnController.EVT_INHALE_END, (d: InhaleEndDetails) => {
      const char = d.side === 'player' ? this.playerChar : this.aiChar;
      gsap.to(this.hazeOverlay, { alpha: 0, duration: 0.25 });
      ctx.audio.stopLoop('sfx_ember');
      const sign: 1 | -1 = d.side === 'player' ? 1 : -1;
      const mouthX = char.x + sign * 40;
      const mouthY = char.y - 110;
      if (d.reason === 'choked' || d.reason === 'joint-burned' || d.reason === 'lung-empty') {
        this.hud.hideComboRing();
        char.setHead('choke');
        char.triggerCoughShake();
        ctx.audio.play('sfx_cough');
        this.smoke.cough(mouthX, mouthY);
        this.joint.setEmber('idle');
        this.hud.setCurrentDrag(d.side, 0);
        // Auto-reset the choke face after 3 seconds (or sooner if TURN_START
        // for this side fires first — see the TURN_START handler which also
        // clears the timer and restores idle).
        this.cancelChokeResetTimer(d.side);
        const handle = window.setTimeout(() => {
          this.chokeResetTimers.delete(d.side);
          if (char.getHead() === 'choke') char.setHead('idle');
        }, 3000);
        this.chokeResetTimers.set(d.side, handle);
      } else {
        char.setHead('exhale');
        ctx.audio.play('sfx_exhale');
        this.smoke.exhale(mouthX, mouthY, sign);
        this.hud.setCurrentDrag(d.side, 0);
        gsap.delayedCall(0.45, () => {
          if (this.turnCtrl.getActiveSide() === d.side) char.setHead('idle');
        });
        this.joint.setEmber('idle');
      }
    });

    this.turnCtrl.on(TurnController.EVT_COMBO_WINDOW_START, (side: Side, durationMs: number) => {
      this.hud.showComboRing(durationMs);
      if (side === 'player' && isKeyDown('Space')) {
        gsap.delayedCall(0.08, () => {
          if (this.turnCtrl.getState() === 'combo-window-player') this.turnCtrl.onSpaceDown();
        });
      }
    });

    this.turnCtrl.on(TurnController.EVT_COMBO_WINDOW_END, () => {
      this.hud.hideComboRing();
    });

    this.turnCtrl.on(TurnController.EVT_PASS_START, (fromSide: Side, toSide: Side) => {
      const fromChar = fromSide === 'player' ? this.playerChar : this.aiChar;
      const toChar = toSide === 'player' ? this.playerChar : this.aiChar;

      this.cancelChokeResetTimer(fromSide);

      // Both characters pre-pose for the handoff. Setting both to `passing`
      // (extended-arm) gives us real, measurable grip anchors to fly between.
      fromChar.setArm('passing');
      fromChar.setHead('idle');
      toChar.setArm('passing');
      toChar.setHead('idle');

      this.hud.hideComboRing();
      this.hud.setTurnIndicator(toSide);
      this.joint.setHolder(fromSide); // Joint orientation stays with from through the arc.

      // Real anchor points taken straight from each character's arm SVG.
      // `getJointAnchorWorld` returns the fingertip in world space for the
      // current arm state (`passing`).
      const start = fromChar.getJointAnchorWorld();
      const end = toChar.getJointAnchorWorld();

      const passDurationSecs = TUNING.turn.passAnimationMs / 1000;
      const midTime = 0.5;

      // Arc apex: midpoint between the two fingertips, raised for a toss feel.
      const midX = (start.x + end.x) / 2;
      const midY = Math.min(start.y, end.y) - 80;

      const fromBaseX = fromChar.x;
      const toBaseX = toChar.x;
      // Subtle body language — the passer leans slightly toward the receiver,
      // the receiver leans in a bit. Values are tiny so the fingertip anchors
      // don't drift far from what `getJointAnchorWorld` reported.
      gsap.to(fromChar, {
        x: fromBaseX + (fromSide === 'player' ? 1 : -1) * 8,
        duration: passDurationSecs * midTime,
        yoyo: true,
        repeat: 1,
        ease: 'sine.inOut',
      });
      gsap.to(toChar, {
        x: toBaseX + (toSide === 'player' ? -1 : 1) * 6,
        duration: passDurationSecs * midTime,
        yoyo: true,
        repeat: 1,
        ease: 'sine.inOut',
      });

      const jointBaseRotation = this.joint.rotation ?? 0;
      const tl = gsap.timeline({
        onComplete: () => {
          this.joint.rotation = jointBaseRotation;
        },
      });

      // Segment A: rise from the passer's fingertip to the arc apex.
      tl.to(this.joint, {
        x: midX,
        y: midY,
        duration: passDurationSecs * midTime,
        ease: 'sine.out',
      });
      // Segment B: fall from the apex into the receiver's fingertip.
      tl.to(this.joint, {
        x: end.x,
        y: end.y,
        duration: passDurationSecs * (1 - midTime),
        ease: 'sine.in',
      });
      // Joint tumbles slightly during the arc for flavor.
      tl.to(this.joint, {
        rotation: jointBaseRotation + (fromSide === 'player' ? 1 : -1) * Math.PI * 0.4,
        duration: passDurationSecs,
        ease: 'power1.inOut',
      }, 0);
    });

    this.turnCtrl.on(TurnController.EVT_PASS_END, (toSide: Side) => {
      const toChar = toSide === 'player' ? this.playerChar : this.aiChar;
      const fromChar = toSide === 'player' ? this.aiChar : this.playerChar;
      // Receiver grips, passer relaxes.
      fromChar.setArm('idle');
      toChar.setArm('holding');
      this.joint.setHolder(toSide);
      // Snap the joint to the holding fingertip immediately. A tween here
      // would race INHALE_START's placeJointInHand if the user presses
      // SPACE within ~140ms, leaving the joint stranded at the passing-arm
      // anchor until the next state change fires. Snapping is more reliable
      // and is fast enough (< 1 frame) to read as a natural handoff.
      gsap.killTweensOf(this.joint);
      this.joint.rotation = 0;
      this.placeJointInHand(toSide);
    });

    this.turnCtrl.on(TurnController.EVT_TURN_START, (side: Side) => {
      this.hud.setTurnIndicator(side);
      this.hud.setAtRisk('player', 0);
      this.hud.setAtRisk('ai', 0);
      this.hud.setCurrentDrag('player', 0);
      this.hud.setCurrentDrag('ai', 0);
      // If this side's turn comes back around before the 3s choke reset
      // timer fires, clear the pending timer and restore the idle face now.
      this.cancelChokeResetTimer(side);
      const char = side === 'player' ? this.playerChar : this.aiChar;
      if (char.getHead() === 'choke') char.setHead('idle');
    });

    this.turnCtrl.on(TurnController.EVT_GAME_OVER, () => this.endMatch());

    // --- QTE events ---
    this.qteCtrl.on(QteController.EVT_ARMED, (d: QteArmedDetails) => {
      this.hud.setQteArmedHint(d.side, true);
    });

    this.qteCtrl.on(QteController.EVT_START, (d: QteStartDetails) => {
      this.hud.showQte(d.goldenStartPct, d.goldenEndPct);
    });

    this.qteCtrl.on(QteController.EVT_WIN, (d: QteWinDetails) => {
      this.hud.flashQteWin(d.pctReduced);
      this.hud.setQteArmedHint(d.side, false);
      ctx.audio.play('sfx_click', { volume: 0.8 });
    });

    this.qteCtrl.on(QteController.EVT_MISS, (d: QteMissDetails) => {
      this.hud.hideQte();
      this.hud.setQteArmedHint(d.side, false);
    });

    this.qteCtrl.on(QteController.EVT_END, (d: QteEndDetails) => {
      if (!d.won) this.hud.hideQte();
    });
  }

  private bindKeyboard(): void {
    const downOff = onKeyDown('Space', () => this.turnCtrl.onSpaceDown());
    const upOff = onKeyUp('Space', () => this.turnCtrl.onSpaceUp());
    this.onCleanup(downOff);
    this.onCleanup(upOff);
  }

  private bindTouch(): void {
    if (!isMobile()) return;
    this.touchCtrl = new TouchController(
      () => this.turnCtrl.onSpaceDown(),
      () => this.turnCtrl.onSpaceUp()
    );
    this.touchCtrl.container.zIndex = 150;
    this.addChild(this.touchCtrl.container);
  }

  // ── Coin flip intro ──────────────────────────────────────────────────────

  private runCoinFlip(): void {
    this.hud.setTurnIndicator(null);

    const cx = GAME_WIDTH / 2;
    const cy = GAME_HEIGHT / 2;

    // Container at world-center so children animate with local coords.
    const coinContainer = new Container();
    coinContainer.position.set(cx, cy);
    coinContainer.zIndex = 200;
    this.addChild(coinContainer);

    // Flip coin — wrap the sprite in an inner container we scale once for
    // size. The coin sprite itself keeps scale.set(1,1) so the GSAP tween
    // that animates scale.x for the flip effect has a clean range to work
    // in. Applying both sizing and flip to the same object would cause the
    // flip tween to get clobbered by sizing writes.
    const coinSizer = new Container();
    // Target display size ≈ 200×200. The SVG is 200×200 native so the sizer
    // needs scale 1.0; but we scale by 1.0 explicitly for clarity and to
    // make the intent obvious.
    coinSizer.scale.set(1.0);
    coinContainer.addChild(coinSizer);

    const coinSprite = new Sprite(Texture.from('ui_flip_coin'));
    coinSprite.anchor.set(0.5);
    // DON'T set width/height — that writes to scale and would fight the tween.
    // The native SVG size is already 200×200 (see assets.ts). If the texture
    // hasn't resolved yet, Pixi will repaint when it does.
    coinSizer.addChild(coinSprite);

    const banner = new Text({
      text: 'Flipping a coin…',
      style: { fontFamily: 'Inter, sans-serif', fontSize: 30, fill: 0xfce0bd },
    });
    banner.anchor.set(0.5);
    banner.position.set(cx, cy + 160);
    banner.zIndex = 200;
    this.addChild(banner);

    // Pre-roll the side so match state is deterministic even if any animation
    // is skipped or killed. This is the ONLY place the side is decided.
    const first: Side = Math.random() < 0.5 ? 'player' : 'ai';

    // Spin animation: continuous rotation of the entire coin+sizer together.
    // This is a simple, reliable GSAP target — a single number going from 0
    // to N×2π — with no interaction between sizing and flip math.
    coinSizer.rotation = 0;
    const spinTween = gsap.to(coinSizer, {
      rotation: Math.PI * 6, // 3 full spins over the spin duration
      duration: 1.1,
      ease: 'power2.out',
    });
    // Gentle vertical bob synced with the spin.
    const bobTween = gsap.to(coinContainer, {
      y: cy - 18,
      duration: 0.22,
      ease: 'sine.inOut',
      yoyo: true,
      repeat: 4,
    });

    // Resolution is scheduled on a FIXED wall-clock timer — independent of
    // the tween's onComplete, so the match ALWAYS starts even if a tween is
    // killed mid-flight or fails to fire its callback. This is the robust
    // path that fixes the "never chose a starter" bug.
    const resolveMs = 1150;
    const resolveTimeout = window.setTimeout(() => {
      spinTween.kill();
      bobTween.kill();
      gsap.killTweensOf(coinContainer);
      gsap.killTweensOf(coinSizer);
      if (!coinContainer.parent) return; // Scene torn down mid-flip.

      // Settle the coin pose before it's replaced by the winner label.
      coinSizer.rotation = 0;
      coinContainer.y = cy;

      // Swap coin → winner label.
      coinSizer.visible = false;
      const winnerText = new Text({
        text: first === 'player' ? 'YOU' : 'BOT',
        style: {
          fontFamily: 'Impact, sans-serif',
          fontSize: 140,
          fill: 0xffd23f,
          stroke: { color: 0x1a0d22, width: 7 },
        },
      });
      winnerText.anchor.set(0.5);
      winnerText.alpha = 0;
      winnerText.scale.set(0.5);
      coinContainer.addChild(winnerText);

      banner.text = first === 'player' ? 'You start!' : 'Bot starts!';

      // Apply the match state IMMEDIATELY — the holder, arms, joint — so
      // even if the exit animation is interrupted, the game is in a valid
      // state and turnCtrl.beginMatch below always runs.
      this.playerChar.setArm(first === 'player' ? 'holding' : 'idle');
      this.aiChar.setArm(first === 'ai' ? 'holding' : 'idle');
      this.joint.setHolder(first);
      this.placeJointInHand(first);

      gsap.to(winnerText, { alpha: 1, duration: 0.24, ease: 'sine.out' });
      gsap.to(winnerText.scale, { x: 1, y: 1, duration: 0.35, ease: 'back.out(2.4)' });

      // Fixed-timer handoff to the match — not chained off a tween callback.
      const handoffTimeout = window.setTimeout(() => {
        this.coinFlipTimeoutHandles.delete(handoffTimeout);
        // Capture positions BEFORE creating tweens so the tween target values
        // are plain numbers, not property reads that could hit a destroyed
        // Pixi object mid-flight.
        if (coinContainer.parent && coinContainer.position) {
          const targetY = coinContainer.y - 40;
          gsap.to(coinContainer, { alpha: 0, y: targetY, duration: 0.3 });
        }
        if (banner.parent && banner.position) {
          const targetY = banner.y - 40;
          gsap.to(banner, { alpha: 0, y: targetY, duration: 0.3 });
        }
        const cleanupTimeout = window.setTimeout(() => {
          this.coinFlipTimeoutHandles.delete(cleanupTimeout);
          if (coinContainer.parent) {
            gsap.killTweensOf(coinContainer);
            coinContainer.destroy({ children: true });
          }
          if (banner.parent) {
            gsap.killTweensOf(banner);
            banner.destroy();
          }
        }, 320);
        this.coinFlipTimeoutHandles.add(cleanupTimeout);
        this.turnCtrl.beginMatch(first);
      }, 950);
      this.coinFlipTimeoutHandles.add(handoffTimeout);
    }, resolveMs);
    this.coinFlipTimeoutHandles.add(resolveTimeout);
  }

  private cancelChokeResetTimer(side: Side): void {
    const existing = this.chokeResetTimers.get(side);
    if (existing !== undefined) {
      window.clearTimeout(existing);
      this.chokeResetTimers.delete(side);
    }
  }

  private placeJointInHand(side: Side): void {
    const char = side === 'player' ? this.playerChar : this.aiChar;
    const anchor = char.getJointAnchorWorld();
    this.joint.position.set(anchor.x, anchor.y);
  }

  private jointTipWorldPosition(): { x: number; y: number } {
    const sign = this.turnCtrl.getActiveSide() === 'player' ? 1 : -1;
    const ratio = this.jointCtrl.getLengthPct() / 100;
    const localX =
      (TUNING.joint.paperVisualLengthPx * ratio + TUNING.joint.filterVisualLengthPx) * sign;
    const localY = -6;
    const cos = Math.cos(this.joint.rotation);
    const sin = Math.sin(this.joint.rotation);
    return {
      x: this.joint.x + localX * cos - localY * sin,
      y: this.joint.y + localX * sin + localY * cos,
    };
  }

  private endMatch(): void {
    const ctx = getContext();
    const winner = this.scoreCtrl.determineWinner();
    if (winner === 'player') {
      this.playerChar.setHead('win');
      this.playerChar.triggerCelebrate();
      this.aiChar.setHead('lose');
      this.aiChar.triggerSlump();
      ctx.audio.play('sfx_win');
    } else if (winner === 'ai') {
      this.aiChar.setHead('win');
      this.aiChar.triggerCelebrate();
      this.playerChar.setHead('lose');
      this.playerChar.triggerSlump();
      ctx.audio.play('sfx_lose');
    } else {
      this.playerChar.setHead('idle');
      this.aiChar.setHead('idle');
    }
    ctx.audio.stopAllLoops();
    gsap.delayedCall(1.5, () => {
      ctx.audio.stopMusic();
      ctx.sceneManager.start('GameOverScene', {
        difficulty: this.difficulty,
        snapshot: this.scoreCtrl.getSnapshot(),
        winner,
      });
    });
  }
}
